import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link, useLocation } from "react-router-dom";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isBigger } from "store/actions/device";
import { setOpenMenu } from "store/actions/menu";
import {
  STORAGE_OPEN_GROUPS,
  SYSTEM_GROUP_ID,
  buildMenu,
  findActive,
  isItemActive,
  readStorage,
  writeStorage,
} from "layout/menuConfig";
import { AdminDrawer } from "./theme";

// Groups open by default on first use: every group except "Sistema".
const defaultOpenGroups = (groups) => groups
  .filter((group) => !group.path && group.id !== SYSTEM_GROUP_ID)
  .map((group) => group.id);

const EntryButton = ({ item, selected, nested, onNavigate }) => (
  <ListItemButton
    component={Link}
    to={item.path}
    selected={selected}
    onClick={onNavigate}
    sx={{ pl: nested ? 7 : 2, py: nested ? 0.5 : 1 }}
  >
    {!nested && <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>}
    <ListItemText primary={item.label} primaryTypographyProps={{ variant: nested ? "body2" : "body1" }} />
  </ListItemButton>
);

// Icons-only entry: single entries get a tooltip, groups a menu with their entries.
const MiniEntry = ({ group, activePath, onNavigate }) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  const active = group.path ? activePath === group.path : group.children.some((child) => child.path === activePath);

  if (group.path) {
    return (
      <Tooltip title={group.label} placement="right">
        <ListItemButton
          component={Link}
          to={group.path}
          selected={active}
          onClick={onNavigate}
          aria-label={group.label}
          sx={{ justifyContent: "center", px: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>{group.icon}</ListItemIcon>
        </ListItemButton>
      </Tooltip>
    );
  }
  return (
    <>
      <ListItemButton
        selected={active}
        onClick={(event) => setAnchor(event.currentTarget)}
        onMouseEnter={(event) => setAnchor(event.currentTarget)}
        aria-label={group.label}
        aria-haspopup="menu"
        sx={{ justifyContent: "center", px: 2 }}
      >
        <ListItemIcon sx={{ minWidth: 0 }}>{group.icon}</ListItemIcon>
      </ListItemButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { onMouseLeave: close, sx: { minWidth: 220 } } }}
        MenuListProps={{ subheader: <ListSubheader>{group.label}</ListSubheader> }}
      >
        {group.children.map((child) => (
          <MenuItem
            key={child.path}
            component={Link}
            to={child.path}
            selected={child.path === activePath}
            onClick={() => { close(); onNavigate(); }}
          >
            {child.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export function MainMenu({ isOpen, device, routes, mini, setOpenMenu }) {
  const location = useLocation();
  const desktop = isBigger(device, "md");
  const groups = useMemo(() => buildMenu(routes), [routes]);
  const active = useMemo(() => findActive(groups, location.pathname), [groups, location.pathname]);
  const [openGroups, setOpenGroups] = useState(() => readStorage(STORAGE_OPEN_GROUPS, null));
  const initialised = useRef(false);

  // First render without a stored preference: open everything but "Sistema".
  useEffect(() => {
    if (initialised.current || groups.length === 0) return;
    initialised.current = true;
    if (openGroups === null) setOpenGroups(defaultOpenGroups(groups));
  }, [groups, openGroups]);

  // The group of the current page is always open.
  useEffect(() => {
    if (!active || active.groupId === "dashboard") return;
    setOpenGroups((current) => {
      const list = current || defaultOpenGroups(groups);
      if (list.includes(active.groupId)) return current;
      const next = [...list, active.groupId];
      writeStorage(STORAGE_OPEN_GROUPS, next);
      return next;
    });
  }, [active, groups]);

  const toggleGroup = (groupId) => setOpenGroups((current) => {
    const list = current || defaultOpenGroups(groups);
    const next = list.includes(groupId) ? list.filter((id) => id !== groupId) : [...list, groupId];
    writeStorage(STORAGE_OPEN_GROUPS, next);
    return next;
  });

  // On small screens the drawer is temporary and closes after a navigation.
  const onNavigate = useCallback(() => {
    if (!desktop) setOpenMenu(false);
  }, [desktop, setOpenMenu]);

  if (routes === null) return null;

  const opened = openGroups || defaultOpenGroups(groups);
  const activePath = active ? active.path : null;
  const miniMode = desktop && mini;

  const content = (
    <List component="nav" aria-label="Menu principale" disablePadding>
      {groups.map((group, index) => {
        const isOpenGroup = opened.includes(group.id);
        const groupActive = Boolean(active && active.groupId === group.id);
        return (
          <React.Fragment key={group.id}>
            {index > 0 && <Divider />}
            {miniMode ? (
              <MiniEntry group={group} activePath={activePath} onNavigate={onNavigate} />
            ) : group.path ? (
              <EntryButton item={group} selected={groupActive} onNavigate={onNavigate} />
            ) : (
              <>
                <ListItemButton
                  onClick={() => toggleGroup(group.id)}
                  selected={groupActive && !isOpenGroup}
                  aria-expanded={isOpenGroup}
                  aria-controls={`menu-group-${group.id}`}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{group.icon}</ListItemIcon>
                  <ListItemText primary={group.label} />
                  {isOpenGroup ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </ListItemButton>
                <Collapse in={isOpenGroup} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding id={`menu-group-${group.id}`}>
                    {group.children.map((child) => (
                      <EntryButton
                        key={child.path}
                        item={child}
                        nested
                        selected={isItemActive(child, location.pathname)}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );

  return (
    <AdminDrawer>
      <Drawer
        className={`os-admin-drawer${miniMode ? " mini" : ""}`}
        variant={desktop ? "permanent" : "temporary"}
        anchor="left"
        open={desktop ? true : isOpen}
        onClose={() => setOpenMenu(false)}
        ModalProps={{ keepMounted: true }}
      >
        {content}
      </Drawer>
    </AdminDrawer>
  );
}

const mapStateToProps = (state) => ({
  isOpen: state.menu.isOpen,
  mini: Boolean(state.menu.mini),
  device: state.device.type,
  routes: state.routing.routes,
});

const mapDispatchToProps = (dispatch) => ({
  setOpenMenu: bindActionCreators(setOpenMenu, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainMenu);
